import { ScryfallCard, ScryfallList } from "@/types/scryfall";

export interface ScryfallSearchResult extends Omit<ScryfallList, 'next_page'> {
  next_page?: number;
}

export class ScryfallClient {
  private apiBaseUrl: string;
  private onApiFailure?: () => void;
  private requestQueue: Array<() => Promise<unknown>> = [];
  private isProcessingQueue = false;
  private lastRequestTime = 0;
  private readonly REQUEST_DELAY = 50; // 50ms delay between requests

  constructor(apiBaseUrl: string, onApiFailure?: () => void) {
    this.apiBaseUrl = apiBaseUrl;
    this.onApiFailure = onApiFailure;
  }

  private handleApiFailure = () => {
    if (this.onApiFailure) {
      this.onApiFailure();
    }
  };

  /**
   * Add a request to the queue and process it with proper delays
   */
  private async queueRequest<T>(requestFn: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      this.requestQueue.push(async () => {
        try {
          const result = await requestFn();
          resolve(result);
        } catch (error) {
          reject(error);
        }
      });

      this.processQueue();
    });
  }

  /**
   * Process the request queue with delays
   */
  private async processQueue() {
    if (this.isProcessingQueue || this.requestQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;

    while (this.requestQueue.length > 0) {
      const now = Date.now();
      const timeSinceLastRequest = now - this.lastRequestTime;

      // Ensure minimum delay between requests
      if (timeSinceLastRequest < this.REQUEST_DELAY) {
        await new Promise(resolve => setTimeout(resolve, this.REQUEST_DELAY - timeSinceLastRequest));
      }

      const request = this.requestQueue.shift();
      if (request) {
        try {
          await request();
        } catch (error) {
          // Individual request errors are handled by the caller
          console.warn('Request failed in queue:', error);
        }
        this.lastRequestTime = Date.now();
      }
    }

    this.isProcessingQueue = false;
  }

  private async fetchWithStatusCheck(url: string, options?: RequestInit) {
    try {
      const response = await fetch(url, {
        ...options,
        referrerPolicy: 'no-referrer',
      });

      // Only trigger failure for 5xx errors or network failures
      // 4xx errors are client errors, not API issues
      if (response.status >= 500) {
        this.handleApiFailure();
      }

      return response;
    } catch (error) {
      // Don't flag API as down for aborted requests
      // These are normal user interactions (navigating away, clearing search, etc.)
      if (error instanceof Error && error.name === 'AbortError') {
        // Re-throw without triggering failure
        throw error;
      }

      // Only flag as down for actual network/API failures
      this.handleApiFailure();
      throw error;
    }
  }

  /**
   * Search for cards using the Scryfall search API
   */
  async searchCards(params: {
    query: string;
    sortBy?: string;
    sortOrder?: string;
    page?: number;
    signal?: AbortSignal;
  }): Promise<ScryfallSearchResult> {
    return this.queueRequest(async () => {
      const { query, sortBy = 'name', sortOrder = 'auto', page = 1, signal } = params;

      if (!query.trim()) {
        return {
          object: 'list',
          data: [],
          total_cards: 0,
          has_more: false,
          next_page: undefined
        };
      }

      // Normalize the query to handle spaces after colons in search parameters
      const normalizedQuery = query
        .replace(/\b(\w+):\s+/g, '$1:') // Remove spaces after colons in search parameters
        .trim();

      const searchParams = new URLSearchParams({
        q: `${normalizedQuery} game:paper`,
        format: 'json',
        include_extras: 'false',
        include_multilingual: 'true',
        order: sortBy,
        dir: sortOrder,
        unique: 'cards',
        page: page.toString(),
      });

      const response = await this.fetchWithStatusCheck(
        `${this.apiBaseUrl}/cards/search?${searchParams}`,
        { signal }
      );

      if (!response.ok) {
        if (response.status === 404) {
          return {
            object: 'list',
            data: [],
            total_cards: 0,
            has_more: false,
            next_page: undefined
          };
        }
        throw new Error(`Scryfall API error: ${response.status}`);
      }

      const result: ScryfallList = await response.json();
      return {
        ...result,
        next_page: result.has_more ? page + 1 : undefined,
      };
    });
  }

  /**
   * Get a specific card by ID
   */
  async getCard(cardId: string, signal?: AbortSignal): Promise<ScryfallCard> {
    return this.queueRequest(async () => {
      const response = await this.fetchWithStatusCheck(
        `${this.apiBaseUrl}/cards/${cardId}`,
        { signal }
      );

      if (!response.ok) {
        throw new Error(`Scryfall API error: ${response.status}`);
      }

      return response.json();
    });
  }

  /**
   * Get all printings of a card by oracle ID
   */
  async getCardPrintings(oracleId: string, signal?: AbortSignal): Promise<ScryfallCard[]> {
    return this.queueRequest(async () => {
      const searchParams = new URLSearchParams({
        q: `oracleid:${oracleId}`,
        unique: 'prints',
        order: 'released',
        dir: 'asc',
      });

      const response = await this.fetchWithStatusCheck(
        `${this.apiBaseUrl}/cards/search?${searchParams}`,
        { signal }
      );

      if (!response.ok) {
        throw new Error(`Scryfall API error: ${response.status}`);
      }

      const result: ScryfallList = await response.json();
      return result.data;
    });
  }

  /**
   * Get a card by exact name
   */
  async getCardByName(name: string, signal?: AbortSignal): Promise<ScryfallCard> {
    return this.queueRequest(async () => {
      const response = await this.fetchWithStatusCheck(
        `${this.apiBaseUrl}/cards/named?exact=${encodeURIComponent(name)}`,
        { signal }
      );

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      return response.json();
    });
  }

  /**
   * Get multiple cards by identifiers using the collection endpoint
   */
  async getCardCollection(identifiers: ({ name?: string; set?: string; collector_number?: string })[]): Promise<ScryfallCard[]> {
    return this.queueRequest(async () => {
      const response = await this.fetchWithStatusCheck(`${this.apiBaseUrl}/cards/collection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ identifiers }),
      });
      const data = await response.json();
      return data.data;
    });
  }

  /**
   * Get a random card
   */
  async getRandomCard(signal?: AbortSignal): Promise<ScryfallCard> {
    return this.queueRequest(async () => {
      const response = await this.fetchWithStatusCheck(`${this.apiBaseUrl}/cards/random`, { signal });

      if (!response.ok) {
        throw new Error(`Scryfall API error: ${response.status}`);
      }

      return response.json();
    });
  }
}
